import type { ActivityMessage, AssistantMessage, Message, UserMessage } from '@ag-ui/client';
import is from '@sindresorhus/is';

import type { ChatLiveTurn } from '~/shared/agent-chat';

import {
    type AgUiTurnStatus,
    type AgUiUserMetadata,
    agUiAssistantMetadataSchema,
    agUiBlockActivityType,
    agUiUserMetadataSchema,
    textBlockSchema,
} from '../../data-contract/chat';
import type { ThreadStatus } from '../thread-status';

export type ChatAssistantMessage = ActivityMessage | AssistantMessage;

/**
 * How a turn ended or stands: as persisted, or `on-hold` for a pending turn nobody works on now (a
 * stopped or dropped turn of a resumable thread), which is neither in progress nor complete.
 */
export type ChatTurnStatus = AgUiTurnStatus | 'on-hold';

/**
 * What the thread does now: `working` while a run holds it (another client's, or this client's own),
 * `on-hold` while a turn waits to go on without one, `idle` otherwise.
 */
export type ChatThreadActivity = 'idle' | 'on-hold' | 'working';

/** Everything the agent answered to one user message. */
export type ChatAssistantTurn = {
    readonly createdAt: string;
    /** The persisted assistant message the turn's messages belong to; feedback refers to it. */
    readonly id: string;
    readonly messages: readonly ChatAssistantMessage[];
    readonly status: ChatTurnStatus;
};

export type ChatTranscriptItem =
    | { readonly kind: 'assistant'; readonly turn: ChatAssistantTurn }
    | { readonly kind: 'user'; readonly message: UserMessage };

export const readUserMetadata = (message: UserMessage): AgUiUserMetadata['cobrainer'] | null => {
    const parsed = agUiUserMetadataSchema.safeParse(message.metadata);
    return parsed.success ? parsed.data.cobrainer : null;
};

const readAssistantMetadata = (message: ChatAssistantMessage) => {
    const parsed = agUiAssistantMetadataSchema.safeParse(message.metadata);
    return parsed.success ? parsed.data.cobrainer : null;
};

const isAssistantMessage = (message: Message): message is ChatAssistantMessage =>
    message.role === 'assistant' || message.role === 'activity';

const STATUS_RANK: Readonly<Record<ChatTurnStatus, number>> = { complete: 0, error: 3, 'on-hold': 1, pending: 2 };

/** A turn is as unfinished as its least finished message: an error outweighs a pending one. */
const combineStatus = (first: ChatTurnStatus, second: ChatTurnStatus): ChatTurnStatus =>
    STATUS_RANK[first] >= STATUS_RANK[second] ? first : second;

const PENDING_STATUS_BY_ACTIVITY: Readonly<Record<ChatThreadActivity, ChatTurnStatus>> = {
    idle: 'complete',
    'on-hold': 'on-hold',
    working: 'pending',
};

// A pending turn is in progress only while a run works on it; an empty assistant message without
// readable metadata is a reply that failed without output.
const persistedStatusOf = (
    message: ChatAssistantMessage,
    metadata: ReturnType<typeof readAssistantMetadata>,
    activity: ChatThreadActivity,
): ChatTurnStatus => {
    if (is.null(metadata)) {
        const isEmptyReply = message.role === 'assistant' && !is.nonEmptyStringAndNotWhitespace(message.content);
        return isEmptyReply ? 'error' : 'complete';
    }
    switch (metadata.status) {
        case 'error':
            return 'error';
        case 'pending':
            return PENDING_STATUS_BY_ACTIVITY[activity];
        default:
            return 'complete';
    }
};

const groupTurns = (messages: readonly Message[], activity: ChatThreadActivity): ChatTranscriptItem[] => {
    const items: ChatTranscriptItem[] = [];
    for (const message of messages) {
        if (message.role === 'user') {
            items.push({ kind: 'user', message });
            continue;
        }
        if (!isAssistantMessage(message)) continue;
        const metadata = readAssistantMetadata(message);
        const status = persistedStatusOf(message, metadata, activity);
        const last = items.at(-1);
        const isSameTurn =
            last?.kind === 'assistant' && (is.null(metadata) || metadata.assistantMessageId === last.turn.id);
        if (isSameTurn) {
            items[items.length - 1] = {
                kind: 'assistant',
                turn: {
                    ...last.turn,
                    messages: [...last.turn.messages, message],
                    status: combineStatus(last.turn.status, status),
                },
            };
            continue;
        }
        items.push({
            kind: 'assistant',
            turn: {
                createdAt: metadata?.createdAt ?? '',
                id: metadata?.assistantMessageId ?? message.id,
                messages: [message],
                status,
            },
        });
    }
    return items;
};

// The own run only ever affects the turn it belongs to: the reply to its anchor, or a placeholder
// for a reply that has not produced anything yet.
const applyLiveTurn = (items: ChatTranscriptItem[], live: ChatLiveTurn): ChatTranscriptItem[] => {
    const anchorIndex = items.findIndex((item) => item.kind === 'user' && item.message.id === live.anchorMessageId);
    if (anchorIndex === -1) return items;
    const reply = items[anchorIndex + 1];
    if (reply?.kind === 'assistant') {
        return items.with(anchorIndex + 1, { kind: 'assistant', turn: { ...reply.turn, status: live.status } });
    }
    const placeholder: ChatTranscriptItem = {
        kind: 'assistant',
        turn: { createdAt: '', id: `${live.anchorMessageId}-reply`, messages: [], status: live.status },
    };
    return items.toSpliced(anchorIndex + 1, 0, placeholder);
};

/** What the thread does now, from whether a run holds it and whether this client's own run is in flight. */
export const chatThreadActivityOf = ({
    isOwnRunInFlight,
    threadStatus,
}: {
    readonly isOwnRunInFlight: boolean;
    readonly threadStatus: ThreadStatus;
}): ChatThreadActivity => {
    if (isOwnRunInFlight || threadStatus === 'running') return 'working';
    return threadStatus === 'idle' ? 'idle' : 'on-hold';
};

/** The thread as the chat shows it: user messages and the assistant turn after each, oldest first. */
export const toChatTranscript = (
    messages: readonly Message[],
    live: ChatLiveTurn | null,
    activity: ChatThreadActivity = 'idle',
): readonly ChatTranscriptItem[] => {
    const items = groupTurns(messages, activity);
    return is.null(live) ? items : applyLiveTurn(items, live);
};

export const hasSubstantiveContent = (turn: ChatAssistantTurn): boolean =>
    turn.messages.some((message) => message.role === 'activity' || is.nonEmptyStringAndNotWhitespace(message.content));

const getPlainText = (message: ChatAssistantMessage): string => {
    if (message.role === 'assistant') return message.content ?? '';
    if (message.activityType !== agUiBlockActivityType('text')) return '';
    const block = textBlockSchema.safeParse(message.content);
    if (!block.success) return '';
    return block.data.content.map((element) => (element.type === 'text' ? element.text : element.label)).join('');
};

/** The turn's text as the user can copy it and as feedback quotes it. */
export const toAgentResponseText = (turn: ChatAssistantTurn): string =>
    turn.messages
        .map(getPlainText)
        .map((part) => part.trim())
        .filter((part) => part.length > 0)
        .join('\n');
